import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { ALLOWED_FIELD_KEYS, CONTRACT_TYPES, PROPERTY_TYPES, PROPERTY_TYPE_LABEL, PORTALS } from "@avestate/shared";
import { useListing, usePatchListing } from "@/hooks/useListing.js";
import { useAISuggestions, useRunAIDraft } from "@/hooks/useAISuggestions.js";
import { useCreateApproval } from "@/hooks/useApprovals.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { RoleGate } from "@/components/RoleGate.js";
import { SnapToFieldLayer, RegisterField } from "@/components/signature/SnapToFieldLayer.js";
import { AISuggestionCard, type Suggestion } from "@/components/signature/AISuggestionCard.js";
import { ListingChrome } from "@/components/signature/ListingChrome.js";

type SectionDef = { title: string; keys: readonly string[] };
const SECTIONS: SectionDef[] = [
  { title: "Identità",       keys: ["title", "description", "kind", "features"] },
  { title: "Posizione",      keys: ["city", "zone", "province", "region", "country", "lat", "lng"] },
  { title: "Metriche",       keys: ["mq", "rooms", "bathrooms", "piano"] },
  { title: "Dotazioni",      keys: ["riscaldamento", "esposizione", "stato_conservazione"] },
  { title: "Costi",          keys: ["prezzo", "spese_condominio"] },
  { title: "Energia",        keys: ["ape", "ipe"] },
  { title: "Amministrativi", keys: ["disponibilita", "anno_costruzione"] },
];

const FIELD_LABEL: Record<string, string> = {
  contract: "Contratto", property_type: "Tipologia", comune_istat: "Comune ISTAT",
  title: "Titolo", description: "Descrizione", kind: "Tipo", features: "Caratteristiche",
  city: "Città", zone: "Zona", province: "Provincia", region: "Regione", country: "Paese",
  lat: "Latitudine", lng: "Longitudine",
  mq: "Mq", rooms: "Stanze", bathrooms: "Bagni", piano: "Piano",
  riscaldamento: "Riscaldamento", esposizione: "Esposizione", stato_conservazione: "Stato conservazione",
  prezzo: "Prezzo", spese_condominio: "Spese condominio",
  ape: "Classe energetica (APE)", ipe: "IPE",
  disponibilita: "Disponibilità", anno_costruzione: "Anno costruzione",
};

export default function ListingEditor(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  if (!id) return <p className="text-risk">id mancante</p>;

  const { data: listing, isLoading } = useListing(id);
  const patch = usePatchListing(id);
  const { data: suggestions } = useAISuggestions(id);
  const runDraft = useRunAIDraft(id);
  const createApproval = useCreateApproval(id);

  const [payload, setPayload] = useState<Record<string, unknown>>({});
  const seededRef = useRef<string | null>(null);

  // Prefill the form from the loaded content payload ONCE per listing id.
  // Re-seeding on every refetch (autosave invalidates ["listing"]) would wipe
  // text being typed in another field while the previous save resolves.
  useEffect(() => {
    if (!listing) return;
    if (seededRef.current === id) return;
    seededRef.current = id;
    const pl = (listing.payload ?? {}) as Record<string, unknown>;
    const flat: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(pl)) {
      if ((k === "title" || k === "description") && v && typeof v === "object") {
        flat[k] = (v as Record<string, string>).it ?? "";
      } else if (k === "features" && Array.isArray(v)) {
        flat[k] = (v as string[]).join(", ");
      } else {
        flat[k] = v;
      }
    }
    // Location lives on the listing row (not the content payload) — seed it too.
    const row = listing as unknown as Record<string, unknown>;
    for (const k of ["city", "zone", "province", "region", "country", "lat", "lng", "contract", "property_type", "comune_istat"]) {
      if ((flat[k] == null || flat[k] === "") && row[k] != null) flat[k] = row[k];
    }
    flat.portals_excluded = Array.isArray(row.portals_excluded) ? row.portals_excluded : [];
    setPayload(flat);
  }, [listing, id]);

  function setField(key: string, value: unknown) {
    setPayload((p) => ({ ...p, [key]: value }));
  }

  const NUMERIC = ["prezzo", "mq", "rooms", "bathrooms", "ipe", "lat", "lng", "spese_condominio", "anno_costruzione"];

  function shapeForSave(key: string, raw: unknown): unknown {
    const s = String(raw ?? "");
    if (key === "title" || key === "description") {
      const orig = (listing?.payload?.[key] ?? {}) as Record<string, string>;
      return { ...orig, it: s };
    }
    if (key === "features") return s.split(",").map((x) => x.trim()).filter(Boolean);
    if (key === "property_type" || key === "comune_istat") return s || null;
    if (NUMERIC.includes(key)) {
      if (s.trim() === "") return null;
      const n = Number(s);
      return Number.isFinite(n) ? n : raw;
    }
    return raw;
  }

  // Location keys are listing-row columns (authoritative for public reads), so
  // they patch top-level; everything else is content payload.
  const ROW_KEYS = ["city", "zone", "province", "region", "lat", "lng", "contract", "property_type", "comune_istat"];

  async function saveField(key: string, overrideValue?: unknown) {
    try {
      // Selects call this in the same tick as setField, when `payload` is still
      // the pre-update (stale) value — so accept the fresh value explicitly.
      const raw = overrideValue !== undefined ? overrideValue : payload[key];
      const value = shapeForSave(key, raw);
      if (ROW_KEYS.includes(key)) {
        await patch.mutateAsync({ [key]: value } as never);
      } else {
        if (!ALLOWED_FIELD_KEYS.includes(key as never)) return;
        await patch.mutateAsync({ fields: { [key]: value } as never });
      }
      toast.success(`${FIELD_LABEL[key] ?? key} salvato`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  // portals_excluded is a listing-ROW column (PATCHed top-level), parked in the
  // `payload` map only for UI convenience. It must be saved ONLY via this direct
  // PATCH — it is NOT in ALLOWED_FIELD_KEYS so saveField would drop it.
  async function togglePortalExclusion(key: string) {
    if (patch.isPending) return;
    const cur = Array.isArray(payload.portals_excluded) ? (payload.portals_excluded as string[]) : [];
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    setField("portals_excluded", next);
    try {
      await patch.mutateAsync({ portals_excluded: next } as never);
    } catch (e) {
      toast.error((e as Error).message);
      setField("portals_excluded", cur);
    }
  }

  async function onAcceptSuggestion(s: Suggestion) {
    // Reshape like a manual edit: title/description → i18n record, features →
    // array, numerics → number. Writing the raw scalar corrupts those shapes.
    const shaped = shapeForSave(s.field_key, s.suggested_value);
    setField(s.field_key, s.suggested_value);
    await patch.mutateAsync({ fields: { [s.field_key]: shaped } as never }).catch(() => {});
    await createApproval.mutateAsync({
      claim_key: s.field_key as never,
      decision: "approved",
      comment: `IA · confidence ${s.confidence.toFixed(2)}`,
    }).catch(() => {});
  }
  async function onRejectSuggestion(s: Suggestion) {
    await createApproval.mutateAsync({
      claim_key: s.field_key as never,
      decision: "rejected",
    }).catch(() => {});
  }

  if (isLoading) return <p className="text-ink-mute font-mono text-meta uppercase">carico...</p>;
  if (!listing) return <p className="text-risk">Immobile non trovato</p>;

  return (
    <SnapToFieldLayer>
      <div className="space-y-4">
        <ListingChrome id={id} />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
          {/* LEFT: structured form */}
          <div className="ticks relative h-fit rounded-[5px] border border-rule bg-surface">
            <section className="border-b border-rule-soft">
              <div className="border-b border-rule-soft px-5 py-[10px]"><span className="micro">Classificazione · portali</span></div>
              <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
                <div>
                  <label className="micro mb-[9px] block">Contratto</label>
                  <select
                    className="h-9 w-full rounded-[6px] border border-rule bg-surface-2 px-3 text-body text-ink [&>option]:bg-surface"
                    value={String(payload.contract ?? "vendita")}
                    onChange={(e) => { setField("contract", e.target.value); void saveField("contract", e.target.value); }}
                  >
                    {CONTRACT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="micro mb-[9px] block">Tipologia</label>
                  <select
                    className="h-9 w-full rounded-[6px] border border-rule bg-surface-2 px-3 text-body text-ink [&>option]:bg-surface"
                    value={String(payload.property_type ?? "")}
                    onChange={(e) => { setField("property_type", e.target.value); void saveField("property_type", e.target.value); }}
                  >
                    <option value="">— non impostata —</option>
                    {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{PROPERTY_TYPE_LABEL[t]}</option>)}
                  </select>
                </div>
              </div>
              <div className="border-t border-rule-soft p-5">
                <label className="micro mb-[9px] block">Non pubblicare su</label>
                <div className="flex flex-wrap gap-2">
                  {PORTALS.map((p) => {
                    const excluded = Array.isArray(payload.portals_excluded) && (payload.portals_excluded as string[]).includes(p.key);
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => void togglePortalExclusion(p.key)}
                        disabled={patch.isPending}
                        aria-pressed={excluded}
                        className={`rounded-pill border px-3 py-1 text-[12px] transition-colors disabled:opacity-50 ${excluded ? "border-risk/50 bg-risk/10 text-risk line-through" : "border-rule bg-surface-2 text-ink-soft hover:border-accent hover:text-ink"}`}
                      >
                        {p.name}
                      </button>
                    );
                  })}
                </div>
                <p className="micro mt-2">Gli immobili esclusi non compaiono nel feed di quel portale.</p>
              </div>
            </section>
            {SECTIONS.map((sec) => (
              <section key={sec.title} className="border-b border-rule-soft last:border-b-0">
                <div className="border-b border-rule-soft px-5 py-[10px]">
                  <span className="micro">{sec.title}</span>
                </div>
                <div className="space-y-4 p-5">
                  {sec.keys.map((key) => (
                    <div key={key} className="grid grid-cols-1 gap-2 md:grid-cols-[180px_1fr] md:items-center">
                      <Label htmlFor={key} className="micro">{FIELD_LABEL[key] ?? key}</Label>
                      <RegisterField field_key={key}>
                        {(ref) => (
                          <Input
                            id={key}
                            ref={ref as React.Ref<HTMLInputElement>}
                            value={String(payload[key] ?? "")}
                            onChange={(e) => setField(key, e.target.value)}
                            onBlur={() => void saveField(key)}
                            placeholder={key === "ape" ? "A4 · A3 · ... · G" : ""}
                          />
                        )}
                      </RegisterField>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* RIGHT: AI assistant rail */}
          <aside className="lg:sticky lg:top-[64px] lg:self-start">
            <div className="ticks relative rounded-[5px] border border-rule bg-surface p-4">
              <header className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="size-4 text-ai" />
                  <h2 className="text-h2 font-display text-ink">Assistente IA</h2>
                </div>
                <RoleGate min="editor" asDisabled>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      runDraft.mutate(undefined, {
                        onSuccess: (r) => toast.success(`Bozza generata · ${r.suggestions_kept} suggerimenti`),
                        onError: (e) => toast.error((e as Error).message),
                      });
                    }}
                    disabled={runDraft.isPending}
                  >
                    {runDraft.isPending ? "Genero..." : "Genera bozza"}
                  </Button>
                </RoleGate>
              </header>

              {(!suggestions || suggestions.length === 0) ? (
                <p className="rounded border border-rule bg-surface-2 p-4 text-body text-ink-mute">
                  Nessun suggerimento. Carica foto e appunti, poi clicca <strong>Genera bozza</strong>.
                </p>
              ) : (
                <div className="space-y-3">
                  <AnimatePresence mode="popLayout">
                    {suggestions.map((s) => (
                      <AISuggestionCard
                        key={s.id}
                        suggestion={{
                          id: s.id,
                          field_key: s.field_key,
                          field_label: FIELD_LABEL[s.field_key] ?? s.field_key,
                          suggested_value: s.suggested_value,
                          confidence: s.confidence,
                          source_evidence: s.source_evidence,
                        }}
                        onAccept={onAcceptSuggestion}
                        onReject={onRejectSuggestion}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </aside>
        </div>

      </div>
    </SnapToFieldLayer>
  );
}
